// DCACalculator.js - Dollar Cost Averaging Simulator
import React, { useState } from 'react';
import {
  Calculator,
  TrendingUp,
  DollarSign,
  Calendar,
  RefreshCw,
  Info,
  ArrowRight,
  PiggyBank,
  Target,
  BarChart3,
  AlertTriangle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const DCACalculator = () => {
  const [symbol, setSymbol] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [investmentAmount, setInvestmentAmount] = useState(500);
  const [frequency, setFrequency] = useState('monthly');
  const [startDate, setStartDate] = useState('2020-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockInfo, setStockInfo] = useState(null);

  // Search for stocks as user types
  const searchStocks = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.slice(0, 8)); // Limit to 8 results
        setShowDropdown(true);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery && searchQuery !== symbol) {
        searchStocks(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle stock selection
  const handleSelectStock = (selectedSymbol, name) => {
    setSymbol(selectedSymbol);
    setSearchQuery(`${selectedSymbol} - ${name}`);
    setShowDropdown(false);
    setSearchResults([]);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setShowDropdown(false);
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  // Frequency options
  const frequencies = [
    { id: 'weekly', label: 'Weekly', days: 7 },
    { id: 'biweekly', label: 'Bi-Weekly', days: 14 },
    { id: 'monthly', label: 'Monthly', days: 30 },
    { id: 'quarterly', label: 'Quarterly', days: 91 }
  ];

  // Popular stocks for quick selection
  const popularStocks = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'SPY', 'QQQ', 'VTI'];

  const calculateDCA = async () => {
    if (!symbol.trim()) {
      setError('Please enter a stock symbol');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      // Fetch historical price data
      const response = await fetch(`${API_BASE}/stocks/${symbol.toUpperCase()}/price-history?period=5y`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch stock data. Please check the symbol.');
      }

      const priceData = await response.json();
      
      if (!priceData || !priceData.prices || priceData.prices.length === 0) {
        throw new Error('No price data available for this symbol');
      }

      // Also get current stock info
      try {
        const infoRes = await fetch(`${API_BASE}/stocks/${symbol.toUpperCase()}`);
        if (infoRes.ok) {
          const info = await infoRes.json();
          setStockInfo(info);
        }
      } catch (e) {
        console.log('Could not fetch stock info');
      }

      // Process price data
      const prices = priceData.prices
        .map(p => ({
          date: new Date(p.date),
          price: p.close
        }))
        .filter(p => p.date >= new Date(startDate) && p.date <= new Date(endDate))
        .sort((a, b) => a.date - b.date);

      if (prices.length === 0) {
        throw new Error('No price data available for the selected date range');
      }

      // Calculate DCA
      const frequencyDays = frequencies.find(f => f.id === frequency)?.days || 30;
      
      let totalInvested = 0;
      let totalShares = 0;
      let investments = [];
      let lastInvestmentDate = null;

      prices.forEach((pricePoint, index) => {
        const shouldInvest = !lastInvestmentDate || 
          (pricePoint.date - lastInvestmentDate) >= (frequencyDays * 24 * 60 * 60 * 1000 * 0.9);

        if (shouldInvest) {
          const shares = investmentAmount / pricePoint.price;
          totalInvested += investmentAmount;
          totalShares += shares;
          lastInvestmentDate = pricePoint.date;

          investments.push({
            date: pricePoint.date,
            price: pricePoint.price,
            amount: investmentAmount,
            shares: shares,
            totalShares: totalShares,
            totalInvested: totalInvested,
            portfolioValue: totalShares * pricePoint.price
          });
        }
      });

      // Calculate final values
      const currentPrice = prices[prices.length - 1].price;
      const finalValue = totalShares * currentPrice;
      const totalReturn = finalValue - totalInvested;
      const returnPercent = (totalReturn / totalInvested) * 100;
      const avgCostPerShare = totalInvested / totalShares;

      // Calculate lump sum comparison (if invested all at start)
      const firstPrice = prices[0].price;
      const lumpSumShares = totalInvested / firstPrice;
      const lumpSumValue = lumpSumShares * currentPrice;
      const lumpSumReturn = lumpSumValue - totalInvested;
      const lumpSumReturnPercent = (lumpSumReturn / totalInvested) * 100;

      // Generate chart data points (monthly summaries)
      const chartData = [];
      let currentMonth = null;
      
      investments.forEach(inv => {
        const monthKey = `${inv.date.getFullYear()}-${inv.date.getMonth()}`;
        if (monthKey !== currentMonth) {
          chartData.push({
            date: inv.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
            invested: inv.totalInvested,
            value: inv.portfolioValue
          });
          currentMonth = monthKey;
        }
      });

      setResults({
        symbol: symbol.toUpperCase(),
        totalInvested,
        totalShares,
        avgCostPerShare,
        currentPrice,
        finalValue,
        totalReturn,
        returnPercent,
        numberOfInvestments: investments.length,
        lumpSum: {
          shares: lumpSumShares,
          value: lumpSumValue,
          return: lumpSumReturn,
          returnPercent: lumpSumReturnPercent
        },
        investments,
        chartData,
        startPrice: firstPrice,
        endPrice: currentPrice,
        priceChange: ((currentPrice - firstPrice) / firstPrice) * 100
      });

    } catch (err) {
      console.error('DCA calculation error:', err);
      setError(err.message || 'Failed to calculate DCA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  // Simple chart component
  const SimpleChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => Math.max(d.invested, d.value)));
    const minValue = 0;
    const range = maxValue - minValue;
    const height = 200;
    const width = 100;

    return (
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${data.length * 40 + 60} ${height + 60}`} style={{ width: '100%', minWidth: '400px', height: 'auto' }}>
          {/* Y-axis labels */}
          <text x="5" y="25" fill="#94a3b8" fontSize="10">{formatCurrency(maxValue)}</text>
          <text x="5" y={height / 2 + 15} fill="#94a3b8" fontSize="10">{formatCurrency(maxValue / 2)}</text>
          <text x="5" y={height + 5} fill="#94a3b8" fontSize="10">$0</text>

          {/* Grid lines */}
          <line x1="55" y1="20" x2={data.length * 40 + 55} y2="20" stroke="#334155" strokeWidth="1" />
          <line x1="55" y1={height / 2 + 10} x2={data.length * 40 + 55} y2={height / 2 + 10} stroke="#334155" strokeWidth="1" strokeDasharray="4" />
          <line x1="55" y1={height} x2={data.length * 40 + 55} y2={height} stroke="#334155" strokeWidth="1" />

          {/* Bars */}
          {data.map((d, i) => {
            const investedHeight = range > 0 ? ((d.invested - minValue) / range) * (height - 20) : 0;
            const valueHeight = range > 0 ? ((d.value - minValue) / range) * (height - 20) : 0;
            const x = 60 + i * 40;

            return (
              <g key={i}>
                {/* Invested bar */}
                <rect
                  x={x}
                  y={height - investedHeight}
                  width="15"
                  height={investedHeight}
                  fill="#64748b"
                  rx="2"
                />
                {/* Value bar */}
                <rect
                  x={x + 17}
                  y={height - valueHeight}
                  width="15"
                  height={valueHeight}
                  fill={d.value >= d.invested ? '#22c55e' : '#ef4444'}
                  rx="2"
                />
                {/* X-axis label */}
                {i % Math.ceil(data.length / 8) === 0 && (
                  <text
                    x={x + 16}
                    y={height + 20}
                    fill="#94a3b8"
                    fontSize="9"
                    textAnchor="middle"
                  >
                    {d.date}
                  </text>
                )}
              </g>
            );
          })}

          {/* Legend */}
          <rect x="60" y={height + 35} width="12" height="12" fill="#64748b" rx="2" />
          <text x="77" y={height + 45} fill="#94a3b8" fontSize="10">Invested</text>
          <rect x="140" y={height + 35} width="12" height="12" fill="#22c55e" rx="2" />
          <text x="157" y={height + 45} fill="#94a3b8" fontSize="10">Portfolio Value</text>
        </svg>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-30px',
          right: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Calculator size={28} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>DCA Calculator</h1>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '0.9rem' }}>
            Simulate dollar-cost averaging strategy and compare with lump sum investing
          </p>
        </div>
      </div>

      {/* Educational Info */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Info size={20} color="#34d399" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ color: '#6ee7b7', fontSize: '0.85rem', margin: '0 0 0.5rem 0', fontWeight: '600' }}>
            What is Dollar-Cost Averaging?
          </p>
          <p style={{ color: '#a7f3d0', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
            DCA is an investment strategy where you invest a fixed amount at regular intervals, regardless of price. 
            This reduces the impact of volatility and removes the stress of timing the market.
          </p>
        </div>
      </div>

      {/* Input Form */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        border: '1px solid #334155',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Configure Your DCA Strategy</h3>
        
        {/* Stock Symbol Search */}
        <div style={{ marginBottom: '1rem', position: 'relative' }}>
          <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Stock Symbol
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value.toUpperCase());
                if (e.target.value.length <= 5 && /^[A-Z]*$/.test(e.target.value.toUpperCase())) {
                  setSymbol(e.target.value.toUpperCase());
                }
              }}
              onFocus={() => searchQuery && searchResults.length > 0 && setShowDropdown(true)}
              placeholder="Search by symbol or company name..."
              style={{
                width: '100%',
                padding: '0.75rem',
                paddingRight: '2.5rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            {searchLoading && (
              <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
                <RefreshCw size={18} color="#94a3b8" className="spin" />
              </div>
            )}
          </div>

          {/* Search Dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'rgba(15, 23, 42, 0.98)',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              marginTop: '0.25rem',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 100,
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
            }}>
              {searchResults.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectStock(result.symbol, result.name)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: idx < searchResults.length - 1 ? '1px solid #334155' : 'none',
                    color: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(51, 65, 85, 0.5)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{result.symbol}</div>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: '#94a3b8',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {result.name}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '0.7rem', 
                    color: '#64748b',
                    whiteSpace: 'nowrap'
                  }}>
                    {result.type || 'Stock'}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Quick Select Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
            {popularStocks.map(s => (
              <button
                key={s}
                onClick={() => {
                  setSymbol(s);
                  setSearchQuery(s);
                  setShowDropdown(false);
                }}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: symbol === s ? 'rgba(16, 185, 129, 0.2)' : 'rgba(51, 65, 85, 0.5)',
                  border: symbol === s ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid #475569',
                  borderRadius: '1rem',
                  color: symbol === s ? '#34d399' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Investment Amount & Frequency */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Investment Amount ($)
            </label>
            <input
              type="number"
              value={investmentAmount}
              onChange={(e) => setInvestmentAmount(Number(e.target.value))}
              min="1"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Frequency
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            >
              {frequencies.map(f => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Date Range */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Calculate Button */}
        <button
          onClick={calculateDCA}
          disabled={loading}
          style={{
            width: '100%',
            padding: '1rem',
            background: loading ? '#475569' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {loading ? (
            <>
              <RefreshCw size={20} className="spin" />
              Calculating...
            </>
          ) : (
            <>
              <Calculator size={20} />
              Calculate DCA Returns
            </>
          )}
        </button>

        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            color: '#fca5a5',
            fontSize: '0.85rem'
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.25rem',
              border: '1px solid #334155'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <PiggyBank size={18} color="#94a3b8" />
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Total Invested</span>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(results.totalInvested)}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{results.numberOfInvestments} investments</div>
            </div>

            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.25rem',
              border: '1px solid #334155'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <TrendingUp size={18} color="#94a3b8" />
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Portfolio Value</span>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: results.totalReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                {formatCurrency(results.finalValue)}
              </div>
              <div style={{ fontSize: '0.75rem', color: results.totalReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                {formatPercent(results.returnPercent)}
              </div>
            </div>

            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.25rem',
              border: '1px solid #334155'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <DollarSign size={18} color="#94a3b8" />
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Total Return</span>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: results.totalReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                {formatCurrency(results.totalReturn)}
              </div>
            </div>

            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.25rem',
              border: '1px solid #334155'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Target size={18} color="#94a3b8" />
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Avg Cost/Share</span>
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(results.avgCostPerShare)}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{formatNumber(results.totalShares, 4)} shares</div>
            </div>
          </div>

          {/* DCA vs Lump Sum Comparison */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            border: '1px solid #334155',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BarChart3 size={20} color="#10b981" />
              DCA vs Lump Sum Comparison
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              {/* DCA Results */}
              <div style={{
                padding: '1rem',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '0.5rem'
              }}>
                <div style={{ fontWeight: '600', color: '#34d399', marginBottom: '0.75rem' }}>
                  📈 Dollar-Cost Averaging
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Final Value:</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(results.finalValue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Total Return:</span>
                  <span style={{ fontWeight: '600', color: results.totalReturn >= 0 ? '#22c55e' : '#ef4444' }}>
                    {formatCurrency(results.totalReturn)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Return %:</span>
                  <span style={{ fontWeight: '600', color: results.returnPercent >= 0 ? '#22c55e' : '#ef4444' }}>
                    {formatPercent(results.returnPercent)}
                  </span>
                </div>
              </div>

              {/* Lump Sum Results */}
              <div style={{
                padding: '1rem',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '0.5rem'
              }}>
                <div style={{ fontWeight: '600', color: '#60a5fa', marginBottom: '0.75rem' }}>
                  💰 Lump Sum (All at Start)
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Final Value:</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency(results.lumpSum.value)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Total Return:</span>
                  <span style={{ fontWeight: '600', color: results.lumpSum.return >= 0 ? '#22c55e' : '#ef4444' }}>
                    {formatCurrency(results.lumpSum.return)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Return %:</span>
                  <span style={{ fontWeight: '600', color: results.lumpSum.returnPercent >= 0 ? '#22c55e' : '#ef4444' }}>
                    {formatPercent(results.lumpSum.returnPercent)}
                  </span>
                </div>
              </div>
            </div>

            {/* Winner Badge */}
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: results.totalReturn > results.lumpSum.return ? 'rgba(16, 185, 129, 0.15)' : 'rgba(59, 130, 246, 0.15)',
              borderRadius: '0.5rem',
              textAlign: 'center'
            }}>
              <span style={{ fontWeight: '600', color: results.totalReturn > results.lumpSum.return ? '#34d399' : '#60a5fa' }}>
                {results.totalReturn > results.lumpSum.return 
                  ? '🏆 DCA outperformed Lump Sum by ' + formatCurrency(results.totalReturn - results.lumpSum.return)
                  : '🏆 Lump Sum outperformed DCA by ' + formatCurrency(results.lumpSum.return - results.totalReturn)
                }
              </span>
            </div>
          </div>

          {/* Portfolio Growth Chart */}
          {results.chartData && results.chartData.length > 0 && (
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              border: '1px solid #334155',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
                Portfolio Growth Over Time
              </h3>
              <SimpleChart data={results.chartData} />
            </div>
          )}

          {/* Price Info */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            borderRadius: '0.75rem',
            padding: '1rem',
            border: '1px solid #334155',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Start Price ({startDate})</span>
                <div style={{ fontWeight: '600' }}>{formatCurrency(results.startPrice)}</div>
              </div>
              <ArrowRight size={20} color="#64748b" />
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>End Price ({endDate})</span>
                <div style={{ fontWeight: '600' }}>{formatCurrency(results.endPrice)}</div>
              </div>
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Price Change</span>
                <div style={{ fontWeight: '600', color: results.priceChange >= 0 ? '#22c55e' : '#ef4444' }}>
                  {formatPercent(results.priceChange)}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '0.75rem'
      }}>
        <AlertTriangle size={20} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
          <strong>Educational Tool:</strong> This calculator uses historical data and does not guarantee future results. 
          Past performance is not indicative of future returns. Always do your own research before investing.
        </p>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default DCACalculator;
